import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button } from '@xingternal/button';
import { IconUpload } from '@xingternal/icons';
import { StatusBanner } from '@xingternal/banner';
import { useMediaListener, BREAKPOINTS } from '@xingternal/util';
import styles from './fileUpload.css';
import useFileUpload, {
  FILE_MAX_AMOUNT,
  FILE_MAX_SIZE_MB,
  SUPPORTED_FILE_TYPES,
} from './useFileUpload';
import FilePreviewer from './FilePreviewer';
import useDropZone from './useDropZone';

const FILE_UPLOAD_ID = 'file_upload';

const FileUpload = ({ onFileUploaded, onFileRemoved, onStatusChange, error: validationError }) => {
  const {
    addFiles,
    removeFile,
    files,
    error: uploadError,
    fileMetaById,
  } = useFileUpload({
    onFileUploaded,
    onFileRemoved,
  });

  const onFilesDropped = (droppedFiles) => {
    addFiles(droppedFiles);
  };

  const { dropZoneRef } = useDropZone(onFilesDropped, styles.dragOverState);

  const error = validationError || uploadError;

  const breakpoint = useMediaListener();
  const isMobile = breakpoint === BREAKPOINTS.handheld;

  const openFileDialog = () => {
    document.getElementById(FILE_UPLOAD_ID).click();
  };

  useEffect(() => {
    onStatusChange({
      isUploading: Object.values(fileMetaById).some((fileMeta) => fileMeta.progress < 1),
    });
  }, [fileMetaById]);

  return (
    <div className={styles.inputContainer} ref={dropZoneRef}>
      <div className={styles.topSection}>
        <div className={styles.tutorialText}>
          <p>
            <FormattedMessage
              id="CONTACT_FORM_FILE_UPLOAD_SIZE_INFO"
              values={{
                maxAmount: FILE_MAX_AMOUNT,
                maxFileSize: FILE_MAX_SIZE_MB,
              }}
            />
          </p>
          <p>
            <FormattedMessage id="CONTACT_FORM_FILE_UPLOAD_TYPE_INFO" />
          </p>
        </div>
        <label htmlFor={FILE_UPLOAD_ID} className={styles.uploadLabel}>
          <Button
            size="medium"
            variant={isMobile ? 'secondary' : 'tertiary'}
            type="button"
            onClick={openFileDialog}
            icon={IconUpload}
            id="upload-button"
            disabled={files.length >= 5}
          >
            {!isMobile && <FormattedMessage id="CONTACT_FORM_FILE_UPLOAD_CHOOSE_BUTTON" />}
          </Button>
          <input
            type="file"
            multiple
            accept={SUPPORTED_FILE_TYPES.join(',')}
            onChange={(event) => {
              const { target = {} } = event || {};
              addFiles(target.files);
              target.value = ''; // this is needed in order to keep triggering onChange, even for the same files
            }}
            id={FILE_UPLOAD_ID}
            hidden
          />
        </label>
      </div>
      <FilePreviewer files={files} fileMetaById={fileMetaById} removeFile={removeFile} />
      <StatusBanner variant="error" show={!!error} display="inline">
        {error}
      </StatusBanner>
    </div>
  );
};

FileUpload.propTypes = {
  onFileUploaded: PropTypes.func,
  onFileRemoved: PropTypes.func,
  error: PropTypes.string,
  onStatusChange: PropTypes.func,
};

FileUpload.defaultProps = {
  onFileUploaded: () => null,
  onFileRemoved: () => null,
  onStatusChange: () => null,
  error: null,
};

export default FileUpload;
